import React, { useState, useEffect } from "react";

// Components
import Navbar from "../../components/Navbar";
import YachtFilters from "../../components/YachtFilters";
import YachtCard from "./components/YachtCard";

// Router
import { Link, useLocation } from "react-router-dom";

// Material UI Icons
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

function YachtsScreen() {
    const location = useLocation();
    const [yachts, setYachts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filteredYachts, setFilteredYachts] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentFilters, setCurrentFilters] = useState({
        type: "all",
        lengthRange: "all",
        capacity: "all",
        year: "all",
        priceRange: "all",
        flag: "all",
        motor: "all",
        startDate: "",
        endDate: "",
    });
    const [isSearchResultsMode, setIsSearchResultsMode] = useState(false);

    useEffect(() => {
        // Check if we have search results from navigation state
        if (location.state?.searchResults) {
            console.log("Search results received:", location.state.searchResults);
            const searchResults = location.state.searchResults;
            setYachts(searchResults);
            setFilteredYachts(searchResults);
            setIsSearchResultsMode(true);
            setLoading(false);
            
            // Set initial filters if yacht type was selected
            if (location.state.searchFilters?.yachtType) {
                setCurrentFilters(prev => ({
                    ...prev,
                    type: location.state.searchFilters.yachtType.toString()
                }));
            }
            
            return;
        }

        // Fetch all yachts regardless of login status
        const fetchUserYachts = async () => {
            try {
                // Tüm yatları getir
                console.log('Tüm yatlar getiriliyor');
                const response = await fetch("/rest/yacht/getAllYachts", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });

                if (!response.ok) {
                    throw new Error('Yatlar getirilemedi');
                }

                const responseData = await response.json();
                console.log("API'den gelen yat verileri:", responseData);
                
                // API'den gelen veriyi kontrol et - direkt array döndürüyor
                let yachtData = [];
                if (Array.isArray(responseData)) {
                    yachtData = responseData;
                    console.log(`${yachtData.length} adet yat bulundu`);
                } else if (responseData.value && Array.isArray(responseData.value)) {
                    yachtData = responseData.value;
                    console.log(`${yachtData.length} adet yat bulundu (value property'den)`);
                } else {
                    console.warn("Beklenmeyen veri formatı:", responseData);
                    yachtData = [];
                }

                setYachts(yachtData);
                setFilteredYachts(yachtData);
                setIsSearchResultsMode(false);
                setLoading(false);
                
            } catch (error) {
                console.error("Yatları getirme hatası:", error);
                setError(error.message || 'Yatlar yüklenirken bir hata oluştu.');
                setLoading(false);
            }
        };

        fetchUserYachts();
    }, [location.state]);

    // Arama ve filtreleme mantığını birleştiren fonksiyon
    const applyFiltersAndSearch = (filters, query) => {
        let filtered = yachts;

        // Tarih filtrelemesi
        if (filters.startDate && filters.endDate) {
            filtered = filtered.filter((yacht) => {
                // Eğer yatın rezervasyonları yoksa, müsait demektir
                if (!yacht.reservedDates || yacht.reservedDates.length === 0) {
                    return true;
                }

                // Seçilen tarih aralığında rezervasyon var mı kontrol et
                return !yacht.reservedDates.some((reservation) => {
                    const reservationStart = new Date(reservation.startDate);
                    const reservationEnd = new Date(reservation.endDate);
                    const filterStart = new Date(filters.startDate);
                    const filterEnd = new Date(filters.endDate);

                    return (
                        (filterStart >= reservationStart &&
                            filterStart <= reservationEnd) ||
                        (filterEnd >= reservationStart &&
                            filterEnd <= reservationEnd) ||
                        (filterStart <= reservationStart &&
                            filterEnd >= reservationEnd)
                    );
                });
            });
        }

        // Arama filtrelemesi
        if (query) {
            const searchTerm = query.toLowerCase();
            filtered = filtered.filter(
                (yacht) =>
                    yacht.name.toLowerCase().includes(searchTerm) ||
                    yacht.captain.toLowerCase().includes(searchTerm)
            );
        }

        // Yat tipi filtresi
        if (filters.type !== "all") {
            filtered = filtered.filter((yacht) => yacht.type === parseInt(filters.type));
        }

        // Uzunluk aralığı filtresi
        if (filters.lengthRange !== "all") {
            filtered = filtered.filter((yacht) => {
                const length = parseFloat(yacht.length);
                switch (filters.lengthRange) {
                    case "0-20":
                        return length <= 20;
                    case "20-30":
                        return length > 20 && length <= 30;
                    case "30-40":
                        return length > 30 && length <= 40;
                    case "40+":
                        return length > 40;
                    default:
                        return true;
                }
            });
        }

        // Kapasite filtresi
        if (filters.capacity !== "all") {
            filtered = filtered.filter((yacht) => {
                const capacity = parseInt(yacht.capacity);
                switch (filters.capacity) {
                    case "1-10":
                        return capacity <= 10;
                    case "11-20":
                        return capacity > 10 && capacity <= 20;
                    case "21-30":
                        return capacity > 20 && capacity <= 30;
                    case "30+":
                        return capacity > 30;
                    default:
                        return true;
                }
            });
        }

        // Yıl filtresi
        if (filters.year !== "all") {
            filtered = filtered.filter((yacht) => {
                const year = parseInt(yacht.year);
                switch (filters.year) {
                    case "2020+":
                        return year >= 2020;
                    case "2015-2019":
                        return year >= 2015 && year < 2020;
                    case "2010-2014":
                        return year >= 2010 && year < 2015;
                    case "-2010":
                        return year < 2010;
                    default:
                        return true;
                }
            });
        }

        // Bayrak filtresi
        if (filters.flag !== "all") {
            filtered = filtered.filter(
                (yacht) => yacht.flag === filters.flag
            );
        }

        // Motor filtresi
        if (filters.motor !== "all") {
            filtered = filtered.filter(
                (yacht) => yacht.engineInformation === filters.motor
            );
        }

        setFilteredYachts(filtered);
    };

    const handleFilterChange = (filters) => {
        setCurrentFilters(filters);
        applyFiltersAndSearch(filters, searchQuery);
    };

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        applyFiltersAndSearch(currentFilters, query);
    };



    if (loading) {
        return (
            <div className="min-h-screen relative">
                <div
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(/images/yacht-bg.jpg)`,
                        filter: "brightness(0.9)",
                    }}
                />
                <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />
                <div className="relative z-10 min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen relative">
                <div
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(/images/yacht-bg.jpg)`,
                        filter: "brightness(0.9)",
                    }}
                />
                <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />
                <Navbar />
                <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 max-w-md w-full text-center">
                        <div className="mb-6">
                            <svg className="w-16 h-16 mx-auto text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <h2 className="text-2xl font-bold text-white mb-2">Oops!</h2>
                            <p className="text-white/80">{error}</p>
                        </div>
                        <div className="flex gap-4 justify-center">
                            <Link 
                                to="/login" 
                                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                            >
                                Giriş Yap
                            </Link>
                            <Link 
                                to="/" 
                                className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                            >
                                Ana Sayfa
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative">
            {/* Background Image */}
            <div
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url(/images/yacht-bg-2.jpg)`,
                    filter: "brightness(0.9)",
                }}
            />
            <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />

            {/* Content */}
            <div className="relative z-10">
                {/* <AuthButtons /> */}
                <Navbar />

                {/* Hero Section */}
                <div className="relative pt-36 pb-12 text-center text-white">
                    <h1 className="text-5xl font-bold mb-4">
                        {isSearchResultsMode ? 'Müsait Yatlar' : (localStorage.getItem('authToken') ? 'Premium Yat Koleksiyonu' : 'Tüm Yatlar')}
                    </h1>
                    <p className="text-lg text-white/60">
                        {isSearchResultsMode 
                            ? 'Seçtiğiniz tarih aralığında müsait olan yatlar'
                            : (localStorage.getItem('authToken') 
                                ? 'Akdeniz\'in en lüks yatları ile unutulmaz bir deneyim'
                                : 'Akdeniz\'in en güzel yatları ile unutulmaz bir deneyim'
                            )
                        }
                    </p>
                    {isSearchResultsMode && location.state?.searchFilters && (
                        <div className="mt-4 flex items-center justify-center gap-4 text-sm text-white/80">
                            <span className="flex items-center gap-2">
                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                {new Date(location.state.searchFilters.startDate).toLocaleDateString('tr-TR')}
                                {' - '}
                                {new Date(location.state.searchFilters.endDate).toLocaleDateString('tr-TR')}
                            </span>
                            <button
                                onClick={async () => {
                                    setIsSearchResultsMode(false);
                                    setLoading(true);
                                    try {
                                        const response = await fetch("/rest/yacht/getAllYachts", {
                                            method: "GET",
                                            headers: {
                                                "Content-Type": "application/json"
                                            }
                                        });

                                        if (!response.ok) {
                                            throw new Error('Yatlar getirilemedi');
                                        }

                                        const responseData = await response.json();
                                        let yachtData = [];
                                        if (Array.isArray(responseData)) {
                                            yachtData = responseData;
                                        } else if (responseData.value && Array.isArray(responseData.value)) {
                                            yachtData = responseData.value;
                                        }

                                        setYachts(yachtData);
                                        setFilteredYachts(yachtData);
                                        setCurrentFilters({
                                            type: "all",
                                            lengthRange: "all",
                                            capacity: "all",
                                            year: "all",
                                            priceRange: "all",
                                            flag: "all",
                                            motor: "all",
                                            startDate: "",
                                            endDate: "",
                                        });
                                        setSearchQuery("");
                                    } catch (error) {
                                        setError(error.message || 'Yatlar yüklenirken bir hata oluştu.');
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                Tüm Yatları Göster
                            </button>
                        </div>
                    )}
                </div>

                {/* Filters */}
                <div className="max-w-7xl mx-auto px-4 py-2 relative z-40">
                    <div className="mb-3">
                        <YachtFilters
                            yachts={yachts}
                            onFilterChange={handleFilterChange}
                        />
                    </div>
                </div>

                {/* Search Bar */}
                <div className="max-w-7xl mx-auto px-4 py-2">
                    <div className="relative w-1/3 ml-auto">
                        <input
                            type="text"
                            placeholder="Yat adı veya kaptan adı ile arama yapın..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-blue-500 transition-colors backdrop-blur-xl"
                        />
                        <div className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white/40">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Yachts Grid */}
                <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
                    {/* Sonuç Sayısı */}
                    <div className="text-white/60 mb-6">
                        {isSearchResultsMode 
                            ? `${filteredYachts.length} müsait yat bulundu`
                            : `${filteredYachts.length} yat bulundu`
                        }
                    </div>

                    {/* Yat Listesi */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredYachts.map((yacht) => (
                            <YachtCard key={yacht.id} yacht={yacht} />
                        ))}
                    </div>

                    {/* Sonuç Bulunamadı */}
                    {filteredYachts.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-white/60 text-lg">
                                {isSearchResultsMode 
                                    ? 'Seçilen tarih aralığında müsait yat bulunamadı.'
                                    : 'Seçilen kriterlere uygun yat bulunamadı.'
                                }
                            </div>
                            <div className="flex gap-4 justify-center mt-4">
                                <button
                                    onClick={() => {
                                        const resetFilters = {
                                            type: "all",
                                            lengthRange: "all",
                                            capacity: "all",
                                            year: "all",
                                            priceRange: "all",
                                            flag: "all",
                                            motor: "all",
                                            startDate: "",
                                            endDate: "",
                                        };
                                        setSearchQuery("");
                                        setCurrentFilters(resetFilters);
                                        handleFilterChange(resetFilters);
                                    }}
                                    className="px-6 py-2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-xl transition-colors text-sm"
                                >
                                    Filtreleri Sıfırla
                                </button>
                                {isSearchResultsMode && (
                                    <button
                                        onClick={async () => {
                                            setIsSearchResultsMode(false);
                                            setLoading(true);
                                            try {
                                                const response = await fetch("/rest/yacht/getAllYachts", {
                                                    method: "GET",
                                                    headers: {
                                                        "Content-Type": "application/json"
                                                    }
                                                });

                                                if (!response.ok) {
                                                    throw new Error('Yatlar getirilemedi');
                                                }

                                                const responseData = await response.json();
                                                let yachtData = [];
                                                if (Array.isArray(responseData)) {
                                                    yachtData = responseData;
                                                } else if (responseData.value && Array.isArray(responseData.value)) {
                                                    yachtData = responseData.value;
                                                }

                                                setYachts(yachtData);
                                                setFilteredYachts(yachtData);
                                                setCurrentFilters({
                                                    type: "all",
                                                    lengthRange: "all",
                                                    capacity: "all",
                                                    year: "all",
                                                    priceRange: "all",
                                                    flag: "all",
                                                    motor: "all",
                                                    startDate: "",
                                                    endDate: "",
                                                });
                                                setSearchQuery("");
                                            } catch (error) {
                                                setError(error.message || 'Yatlar yüklenirken bir hata oluştu.');
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                        className="px-6 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-200 hover:text-white rounded-xl transition-colors text-sm"
                                    >
                                        Tüm Yatları Göster
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default YachtsScreen;
